import { ChevronDown, ChevronRight, CircleDashed, Lock, PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen, type LucideIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router'
import { Badge } from '@/components/ui/Badge'
import { Popover } from '@/components/ui/Popover'
import { env } from '@/config/env'
import { allNavItems, prefetchPage, type NavItem } from '@/config/navigation'
import { useT } from '@/i18n'
import { usePresence } from '@/hooks/usePresence'
import { cn } from '@/lib/cn'
import { readJSON, writeJSON } from '@/lib/storage'
import { useStores } from '@/stores'
import type { SessionStore } from '@/stores/SessionStore'
import { isOpenable, isVisible } from './access'
import type { MenuArea, MenuEntry, MenuNode } from './legacyMenu'
import { entryActive, entryVisible, findActiveEntry, flatEntries, flatten, itemsByPath, menuText, nodeVisible, type Locale } from './menuModel'
import { LogoMark } from './Logo'

/** Each folder remembers its own open state. */
const openKey = (id: string) => `mf.ui.sidebar.open.${id}`

const folderIdsOf = (areas: MenuArea[]) => [...areas.map((area) => area.id), ...new Set(flatEntries(areas).flatMap(({ trail }) => trail))]

const rowClass = 'flex h-8 items-center gap-2.5 rounded-[calc(10px*var(--radius-scale))] px-2.5 text-[length:calc(13.5px*var(--text-scale))]'

function EntryLink({ entry, item, active, label, onNavigate }: { entry: Pick<MenuEntry, 'path' | 'search' | 'icon'>; item?: NavItem; active: boolean; label: string; onNavigate?: () => void }) {
  const t = useT()
  const Icon: LucideIcon = item?.icon ?? entry.icon ?? CircleDashed
  if (!item || item.readiness === 'planned') {
    return (
      <span aria-disabled="true" title={item ? t('layout.sidebar.plannedHint') : t('layout.sidebar.soon')} className={cn(rowClass, 'cursor-not-allowed text-muted')}>
        <Icon className="size-4 shrink-0 text-subtle" strokeWidth={1.75} />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <Lock className="size-3 shrink-0 text-subtle" aria-hidden />
        <span className="sr-only">{t('layout.sidebar.notAvailable')}</span>
      </span>
    )
  }
  return (
    <Link
      to={{ pathname: entry.path, search: entry.search ? `?${entry.search}` : '' }}
      onClick={onNavigate}
      onMouseEnter={() => prefetchPage(entry.path)}
      onFocus={() => prefetchPage(entry.path)}
      aria-current={active ? 'page' : undefined}
      title={label}
      className={cn(
        rowClass,
        'relative transition-[background-color,color,box-shadow,transform] duration-[var(--dur-base)] active:scale-[0.98]',
        active
          ? 'bg-glass-solid font-medium text-fg shadow-[0_1px_2px_rgba(0,0,0,0.06),inset_0_1px_0_var(--glass-highlight)] ring-1 ring-line'
          : 'text-fg-2 hover:bg-hover hover:text-fg',
      )}
    >
      <Icon className={cn('size-4 shrink-0', active ? 'text-accent' : 'text-muted')} strokeWidth={1.85} />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {item.readiness === 'transitional' && (
        <span className="size-1.5 rounded-full bg-warning" title={t('layout.sidebar.transitional')} aria-label={t('layout.sidebar.transitional')} />
      )}
    </Link>
  )
}

type TreeProps = {
  nodes: MenuNode[]
  session: SessionStore
  locale: Locale
  isActive: (entry: MenuEntry) => boolean
  /** Omitted in the compact popover, where every folder is shown open. */
  folders?: { isOpen: (id: string) => boolean; toggle: (id: string) => void }
  onNavigate?: () => void
}

function MenuTree({ nodes, session, locale, isActive, folders, onNavigate }: TreeProps) {
  return (
    <div className="flex flex-col gap-px">
      {nodes.filter((node) => nodeVisible(node, session)).map((node) => {
        if (node.kind === 'folder') {
          const open = folders ? folders.isOpen(node.id) : true
          const label = menuText(node, locale)
          return (
            <div key={node.id}>
              {folders ? (
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => folders.toggle(node.id)}
                  className={cn(rowClass, 'w-full gap-1.5 pl-1.5 text-left font-medium text-fg-2 hover:bg-hover hover:text-fg')}
                >
                  <ChevronRight className={cn('size-3.5 shrink-0 text-subtle transition-transform duration-[var(--dur-base)]', open && 'rotate-90')} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{label}</span>
                </button>
              ) : (
                <div className="px-2.5 pb-0.5 pt-1.5 text-[length:calc(11px*var(--text-scale))] font-medium uppercase tracking-[0.06em] text-subtle">{label}</div>
              )}
              {open && (
                <div className={cn(folders && 'ml-[13px] border-l border-line pl-1.5')}>
                  <MenuTree nodes={node.children} session={session} locale={locale} isActive={isActive} folders={folders} onNavigate={onNavigate} />
                </div>
              )}
            </div>
          )
        }
        const key = `${node.path}?${node.search ?? ''}:${node.label}`
        const children = node.children?.filter((child) => entryVisible(child, session)) ?? []
        return (
          <div key={key}>
            {entryVisible(node, session) && (
              <EntryLink entry={node} item={itemsByPath.get(node.path)} active={isActive(node)} label={menuText(node, locale)} onNavigate={onNavigate} />
            )}
            {children.length > 0 && (
              <div className="ml-[17px] border-l border-line pl-1.5">
                <MenuTree nodes={children} session={session} locale={locale} isActive={isActive} onNavigate={onNavigate} />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

const CompactArea = observer(function CompactArea({ area, active, isActive }: { area: MenuArea; active: boolean; isActive: (entry: MenuEntry) => boolean }) {
  const { appearance, session, ui } = useStores()
  const Icon = area.icon
  const label = menuText(area, ui.contentLocale)
  const live = flatten(area.children, []).some(({ entry }) => {
    const item = itemsByPath.get(entry.path)
    return item && isOpenable(item, session)
  })
  return (
    <Popover
      kind="menu"
      side={appearance.settings.sidebarSide === 'right' ? 'left' : 'right'}
      className="max-h-[80vh] w-64 overflow-y-auto"
      trigger={({ toggle, ...aria }) => (
        <button
          type="button"
          onClick={() => toggle()}
          title={label}
          aria-label={label}
          {...aria}
          className={cn(
            'flex size-10 items-center justify-center rounded-[calc(12px*var(--radius-scale))] transition-colors duration-100',
            active ? 'bg-glass-solid text-accent ring-1 ring-line' : live ? 'text-muted hover:bg-hover hover:text-fg' : 'text-subtle hover:bg-hover',
          )}
        >
          <Icon className="size-4.5" strokeWidth={1.85} />
        </button>
      )}
    >
      {(close) => (
        <div>
          <div className="px-2.5 pb-1.5 pt-1 text-[length:calc(12px*var(--text-scale))] font-semibold uppercase text-fg">{label}</div>
          <MenuTree nodes={area.children} session={session} locale={ui.contentLocale} isActive={isActive} onNavigate={close} />
        </div>
      )}
    </Popover>
  )
})

export const Sidebar = observer(function Sidebar() {
  const t = useT()
  const { ui, session, appearance } = useStores()
  const right = appearance.settings.sidebarSide === 'right'
  const ExpandIcon = right ? PanelRightOpen : PanelLeftOpen
  const CollapseIcon = right ? PanelRightClose : PanelLeftClose
  const { pathname, search } = useLocation()
  const current = new URLSearchParams(search)
  const isActive = (entry: MenuEntry) => Boolean(itemsByPath.get(entry.path)) && entryActive(entry, pathname, current, session.menuAreas)
  const activeTrail = findActiveEntry(session, isActive)?.trail ?? []

  const [open, setOpen] = useState<Record<string, boolean>>(() => Object.fromEntries(folderIdsOf(session.menuAreas).map((id) => [id, readJSON<boolean>(openKey(id), false)])))
  // Expand the folders above the active page whenever the route changes; the user may collapse them again.
  const [expandedFor, setExpandedFor] = useState('')
  const routeKey = `${pathname}${search}`
  if (activeTrail.length > 0 && expandedFor !== routeKey) {
    setExpandedFor(routeKey)
    if (activeTrail.some((id) => !open[id])) setOpen((prev) => ({ ...prev, ...Object.fromEntries(activeTrail.map((id) => [id, true])) }))
  }
  const folders = {
    isOpen: (id: string) => Boolean(open[id]),
    toggle: (id: string) => {
      const next = !open[id]
      setOpen((prev) => ({ ...prev, [id]: next }))
      writeJSON(openKey(id), next)
    },
  }

  const collapsed = ui.sidebarCollapsed
  const drawer = usePresence(ui.mobileNavOpen, 200)
  const liveTotal = allNavItems.filter((item) => !item.hidden && isOpenable(item, session)).length
  const allTotal = allNavItems.filter((item) => !item.hidden).length
  const dashboard = itemsByPath.get('/')
  // Settings open from the profile menu, so the navigation leaves them out.
  const areas = session.menuAreas.filter((area) => !area.inAccountMenu && area.children.some((node) => nodeVisible(node, session)))
  const closeDrawer = () => ui.setMobileNav(false)

  const content = (compact: boolean) => (
    <>
      <div className={cn('flex h-15 shrink-0 items-center gap-2.5 px-3.5', compact && 'justify-center px-0')}>
        <LogoMark className={cn(compact && 'h-6')} />
        {!compact && (
          <div className="min-w-0 flex-1 leading-tight">
            <div className="text-[length:calc(14.5px*var(--text-scale))] font-semibold tracking-[-0.015em] text-fg">{t('layout.brand.name')}</div>
            <div className="text-[length:calc(11.5px*var(--text-scale))] text-muted">{t('layout.brand.tagline')}</div>
          </div>
        )}
        {!compact && !env.isProduction && (
          <Badge tone="warning" className="h-5 min-w-0 px-1.5 text-[length:calc(10.5px*var(--text-scale))] uppercase tracking-[0.04em]">
            {env.appEnv}
          </Badge>
        )}
      </div>

      <nav aria-label={t('layout.sidebar.primary')} className={cn('min-h-0 flex-1 pb-3', compact ? 'flex flex-col items-center gap-1 overflow-y-auto px-2' : 'space-y-0.5 overflow-y-auto px-2.5')}>
        {compact ? (
          <>
            {dashboard && isVisible(dashboard, session) && (
              <NavLink
                to="/"
                end
                title={menuText('dashboard', ui.contentLocale)}
                aria-label={menuText('dashboard', ui.contentLocale)}
                className={({ isActive: current }) =>
                  cn(
                    'flex size-10 shrink-0 items-center justify-center rounded-[calc(12px*var(--radius-scale))] transition-colors',
                    current ? 'bg-glass-solid text-accent ring-1 ring-line' : 'text-muted hover:bg-hover hover:text-fg',
                  )
                }
              >
                <dashboard.icon className="size-4.5" strokeWidth={1.85} />
              </NavLink>
            )}
            {areas.map((area) => (
              <CompactArea key={area.id} area={area} active={activeTrail[0] === area.id} isActive={isActive} />
            ))}
          </>
        ) : (
          <>
            {dashboard && isVisible(dashboard, session) && (
              <EntryLink
                entry={{ path: '/' }}
                item={dashboard}
                active={pathname === '/'}
                label={menuText('dashboard', ui.contentLocale)}
                onNavigate={closeDrawer}
              />
            )}
            {areas.map((area) => {
              const areaOpen = folders.isOpen(area.id)
              return (
                <section key={area.id} className="pt-1">
                  <button
                    type="button"
                    aria-expanded={areaOpen}
                    onClick={() => folders.toggle(area.id)}
                    className="flex h-8 w-full items-center gap-2 rounded-[10px] px-1.5 text-left text-[12.5px] font-semibold uppercase tracking-[0.025em] text-fg-2 hover:bg-hover"
                  >
                    <area.icon className="size-4 shrink-0 text-muted" strokeWidth={1.85} aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{menuText(area, ui.contentLocale)}</span>
                    <ChevronDown className={cn('size-3.5 text-subtle transition-transform', !areaOpen && '-rotate-90')} />
                  </button>
                  {areaOpen && (
                    <div className="pb-1 pl-[25px]">
                      <MenuTree nodes={area.children} session={session} locale={ui.contentLocale} isActive={isActive} folders={folders} onNavigate={closeDrawer} />
                    </div>
                  )}
                </section>
              )
            })}
          </>
        )}
      </nav>

      <div className={cn('shrink-0 border-t border-line p-2.5', compact && 'flex justify-center')}>
        {!compact && (
          <div className="mb-2 px-2 pt-1">
            <div className="flex items-baseline justify-between text-[length:calc(12px*var(--text-scale))]">
              <span className="text-muted">{t('layout.sidebar.liveModules')}</span>
              <span className="num text-fg-2">
                {liveTotal}/{allTotal}
              </span>
            </div>
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-hover">
              <div className="h-full rounded-full bg-accent/70" style={{ width: `${(liveTotal / allTotal) * 100}%` }} />
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={() => ui.toggleSidebar()}
          className={cn(
            'hidden h-8 items-center gap-2.5 rounded-[calc(10px*var(--radius-scale))] px-2.5 text-[length:calc(13px*var(--text-scale))] text-muted hover:bg-hover hover:text-fg lg:flex',
            compact ? 'w-10 justify-center px-0' : 'w-full',
          )}
          aria-label={compact ? t('layout.sidebar.expandSidebar') : t('layout.sidebar.collapseSidebar')}
        >
          {compact ? <ExpandIcon className="size-4" /> : <CollapseIcon className="size-4" />}
          {!compact && t('layout.sidebar.collapse')}
        </button>
      </div>
    </>
  )

  return (
    <>
      <aside
        className={cn(
          'glass glass-sheen hidden shrink-0 flex-col overflow-hidden rounded-[var(--radius-shell)] lg:flex',
          'transition-[width] duration-[var(--dur-slow)] ease-[cubic-bezier(0.22,1,0.36,1)]',
          collapsed ? 'w-[68px]' : 'w-[264px]',
        )}
      >
        {/* Re-keyed so the layout swap cross-fades while the width animates. */}
        <div key={String(collapsed)} className="animate-fade-in flex min-h-0 flex-1 flex-col">
          {content(collapsed)}
        </div>
      </aside>

      {drawer.mounted && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            data-state={drawer.state}
            className="motion-overlay absolute inset-0 bg-black/25 backdrop-blur-[2px]"
            onClick={() => ui.setMobileNav(false)}
            aria-hidden
          />
          <aside
            data-state={drawer.state}
            className={cn(
              'glass-strong absolute inset-y-2 flex w-[284px] flex-col rounded-[var(--radius-shell)]',
              right ? 'motion-sheet right-2' : 'motion-drawer left-2',
            )}
          >
            {content(false)}
          </aside>
        </div>
      )}
    </>
  )
})
